/**
 * Mapbox Geocoding API Adapter
 * Implements geocoding services using Mapbox's Geocoding API
 */
class MapboxGeocodingAdapter {
  constructor(
    baseUrl = process.env.REACT_APP_MAPBOX_GEOCODER_BASE_URL,
    accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN
  ) {
    this.baseUrl = baseUrl
    this.accessToken = accessToken
  }

  async searchLocations(query, options = {}) {
    try {
      const encodedQuery = encodeURIComponent(query)
      const params = new URLSearchParams({
        access_token: this.accessToken,
        types: 'address,poi,place',
        limit: '5',
      })
      const country = options.countries || options.country || 'us'
      if (country) {
        params.set('country', country)
      }
      if (options.proximity && options.proximity.length === 2) {
        params.set('proximity', options.proximity.join(','))
      }
      const url = `${this.baseUrl}/${encodedQuery}.json?${params.toString()}`

      const response = await fetch(url)

      if (!response.ok) {
        throw new Error(`Mapbox API error: ${response.status}`)
      }

      const data = await response.json()

      return {
        data: data.features.map((feature) =>
          this.normalizeFeatureToNominatim(feature)
        ),
      }
    } catch (error) {
      console.error('Error searching locations:', error)
      return { data: [] }
    }
  }

  async reverseGeocode(coordinates) {
    try {
      const url = `${this.baseUrl}/${coordinates.longitude},${coordinates.latitude}.json?access_token=${this.accessToken}&types=address,poi,place&limit=1`

      const response = await fetch(url)

      if (!response.ok) {
        throw new Error(`Mapbox API error: ${response.status}`)
      }

      const data = await response.json()

      if (!data.features || data.features.length === 0) {
        throw new Error('No location found for coordinates')
      }

      const feature = data.features[0]

      return { data: this.normalizeFeatureToNominatim(feature) }
    } catch (error) {
      console.error('Error reverse geocoding:', error)
      throw error
    }
  }

  async forwardGeocode(address) {
    try {
      const encodedAddress = encodeURIComponent(address)
      const url = `${this.baseUrl}/${encodedAddress}.json?access_token=${this.accessToken}&limit=1`

      const response = await fetch(url)

      if (!response.ok) {
        throw new Error(`Mapbox API error: ${response.status}`)
      }

      const data = await response.json()

      if (!data.features || data.features.length === 0) {
        throw new Error('No coordinates found for address')
      }

      const feature = data.features[0]

      return { data: this.normalizeFeatureToNominatim(feature) }
    } catch (error) {
      console.error('Error forward geocoding:', error)
      throw error
    }
  }

  parseGeocodeResponse = (results, lngLat) => {
    if (!(Object.prototype.toString.call(results) === '[object Array]')) {
      results = [results]
    }
    const processedResults = []
    for (const [index, result] of results.entries()) {
      if (
        'error' in result &&
        result.error.toLowerCase() === 'unable to geocode'
      ) {
        processedResults.push({
          title: lngLat.toString(),
          description: '',
          selected: true,
          addresslnglat: '',
          sourcelnglat: lngLat,
          displaylnglat: lngLat,
          key: index,
          addressindex: index,
        })
      } else {
        processedResults.push({
          title:
            result.display_name.length > 0
              ? result.display_name
              : lngLat.toString(),
          description: `https://www.openstreetmap.org/${result.osm_type}/${result.osm_id}`,
          selected: false,
          addresslnglat: [parseFloat(result.lon), parseFloat(result.lat)],
          sourcelnglat:
            lngLat === undefined
              ? [parseFloat(result.lon), parseFloat(result.lat)]
              : lngLat,
          displaylnglat:
            lngLat !== undefined
              ? lngLat
              : [parseFloat(result.lon), parseFloat(result.lat)],
          key: index,
          addressindex: index,
        })
      }
    }
    return processedResults
  }

  normalizeFeatureToNominatim(feature) {
    const displayName = feature.place_name || feature.text || ''
    const [lon, lat] = feature.center || []
    const rawId = feature.id || ''
    const idParts = rawId.toString().split('.')
    const trailingId = idParts[idParts.length - 1]
    const osmId = /^[0-9]+$/.test(trailingId) ? trailingId : rawId

    return {
      display_name: displayName,
      lon: typeof lon === 'number' ? lon.toString() : '',
      lat: typeof lat === 'number' ? lat.toString() : '',
      osm_type: 'node',
      osm_id: osmId,
    }
  }
}

export default MapboxGeocodingAdapter
